import { Box, Button, ButtonProps, Text, Tooltip } from '@chakra-ui/react';
import { ReactComponent as NewTabIcon } from 'assets/icons/new-tab.svg';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { OverLimitError, createE1Design } from 'libs/e1api/infographic';
import { useChameleon } from 'libs/third-party/Chameleon';
import { useToast } from 'modules/common/components/Toast';
import { MouseEvent, ReactElement, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isDevelopment, isProductionDomain } from 'utils/environment';

const E1_DESIGN_BASE_LINK = '/edit';
const TOAST_ID = 'go-classic-toast';

export const GoClassicButtonTooltip = memo(() => {
  const { t } = useTranslation('common_navbar');
  return (
    <Box textAlign='center'>
      <Text>{t('navbar.go-classic-tooltip-p1')}</Text>
      <br />
      <Text>{t('navbar.go-classic-tooltip-p2')}</Text>
      <Text>{t('navbar.go-classic-tooltip-p3')}</Text>
    </Box>
  );
});
GoClassicButtonTooltip.displayName = 'GoClassicButtonTooltip';

export const GoClassicButton = ({ onClick: onClickProp, ...otherProps }: Omit<ButtonProps, 'ref'>): ReactElement => {
  const { t } = useTranslation('common_navbar');
  const { trackChameleon } = useChameleon();

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const { venngageE2 } = useVenngageE1Gate();
  let sourceId: string | undefined = undefined;
  let sourceType: string | undefined = undefined;
  let isE2Only: boolean | undefined = false;

  if (venngageE2 != null) {
    try {
      const data = JSON.parse(venngageE2.sourceData ?? '');

      if (typeof data.converted_from_e1Template === 'string') {
        sourceId = data.converted_from_e1Template;
      }

      // Keep as the reference because the propery exists.
      // if (typeof data.copied_from === 'string') {
      //   sourceId = data.copied_from;
      // }

      if (typeof data.parent_template === 'string') {
        sourceId = data.parent_template;
      }

      if (venngageE2.sourceType === 'string') {
        sourceType = data.sourceType;
      }
    } catch (error) {
      if (isDevelopment) {
        console.error('The design source data is not valid JSON', error);
      }
    }

    sourceType = venngageE2.sourceType;
    isE2Only = venngageE2.isE2Only;
  }

  if (!sourceId || isE2Only) return <></>;

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (isLoading) return;

    setIsLoading(true);
    toast({
      id: TOAST_ID,
      title: t('navbar.go-classic-loading'),
      status: 'info',
      duration: 8000,
    });

    if (!isProductionDomain) {
      console.warn(`This design is created from type ${sourceType} with id ${sourceId}, please confirm the behavior.`);
    }

    createE1Design(sourceId!)
      .then((response) => {
        toast({ title: t('navbar.go-classic-success') });
        trackChameleon('Load Editor1');

        const newId = typeof response.infographic === 'string' ? response.infographic : response.infographic.data.id;
        window.open(`${E1_DESIGN_BASE_LINK}/${newId}`, '_blank');
      })
      .catch((error) => {
        if (error instanceof OverLimitError && error.name === 'OverLimitError') {
          toast({
            title: t('fileMenu.overlimit.title'),
            description: t('fileMenu.overlimit.description'),
            status: 'error',
          });
        } else {
          toast({
            title: t('navbar.go-classic-failure'),
            description: error.message,
            status: 'error',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    onClickProp?.(e);
  };

  return (
    <Tooltip hasArrow placement='bottom' label={<GoClassicButtonTooltip />} minWidth={'max-content'}>
      <Button
        variant='transparent-outline'
        size='sm'
        rightIcon={<NewTabIcon />}
        color='font.500'
        borderColor='gray.200'
        borderRadius='base'
        data-testid='go-classic-button'
        onClick={onClick}
        minW='min-content'
        maxW='max-content'
        disabled={isLoading}
        {...otherProps}
      >
        {t('navbar.go-classic')}
      </Button>
    </Tooltip>
  );
};
